import { useState } from "react";
import type {
  Contract,
  ContractIn,
  EnergyFlow,
  ScenarioParams,
} from "../lib/types";
import { eur, eurPrecise, kwh } from "../lib/format";
import { evaluateContractClient } from "../lib/calculator";

interface Props {
  contract: Contract;
  flow: EnergyFlow;
  scenario: ScenarioParams;
  onSave: (id: number, draft: ContractIn) => void;
  onDelete: (id: number) => void;
  onClose: () => void;
}

export function ContractEditor({ contract, flow, scenario, onSave, onDelete, onClose }: Props) {
  // Strip Contract-only fields (id, created_at, updated_at) — backend ContractIn
  // heeft model_config extra="forbid" en weigert anders met 422.
  const { id: _id, created_at: _created, updated_at: _updated, ...contractIn } = contract;
  void _id; void _created; void _updated;
  const [draft, setDraft] = useState<ContractIn>(contractIn);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const update = <K extends keyof ContractIn>(k: K, v: ContractIn[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const isDynamic = draft.type === "dynamisch";

  // Live preview via client-side calculator
  const preview = evaluateContractClient({ ...draft, id: contract.id }, flow.year_totals, scenario);
  const bd = preview.breakdown;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ background: "rgba(20, 20, 20, 0.4)" }}
      onClick={onClose}
    >
      <div
        className="bg-paper rounded-t-2xl md:rounded-2xl max-w-[600px] w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3.5">
          <h2 className="serif m-0 text-[22px] font-medium">Contract bewerken</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="field mb-3.5">
          <span className="label-up">Naam</span>
          <input
            className="input input-text"
            value={draft.name}
            onChange={(e) => update("name", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3.5">
          <div className="field">
            <span className="label-up">Type</span>
            <div className="toggle-group">
              <button
                className={`toggle-btn ${draft.type === "vast" ? "active" : ""}`}
                onClick={() => update("type", "vast")}
              >
                Vast
              </button>
              <button
                className={`toggle-btn ${isDynamic ? "active" : ""}`}
                onClick={() => update("type", "dynamisch")}
              >
                Dynamisch
              </button>
            </div>
          </div>
          <div className="field">
            <span className="label-up">Vastrecht (€/maand)</span>
            <input
              className="input"
              type="number"
              step="0.01"
              value={draft.vastrecht_eur_per_maand}
              onChange={(e) =>
                update("vastrecht_eur_per_maand", parseFloat(e.target.value) || 0)
              }
            />
          </div>
        </div>

        <hr className="divider" />
        <div className="label-up mb-2.5">Tarieven</div>

        {!isDynamic && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3.5">
            <div className="field">
              <span className="label-up">Kale leveringsprijs (€/kWh)</span>
              <input
                className="input"
                type="number"
                step="0.001"
                value={draft.kale_prijs_afname_eur_per_kwh ?? ""}
                onChange={(e) =>
                  update("kale_prijs_afname_eur_per_kwh", parseFloat(e.target.value) || 0)
                }
              />
            </div>
            <div className="field">
              <span className="label-up">Terugleververgoeding (€/kWh)</span>
              <input
                className="input"
                type="number"
                step="0.001"
                value={draft.terugleververgoeding_eur_per_kwh ?? ""}
                onChange={(e) =>
                  update("terugleververgoeding_eur_per_kwh", parseFloat(e.target.value) || 0)
                }
              />
              <span className="field-help">
                2026: voor het overschot boven saldeerplafond. 2027: hier of min 50% kale prijs.
              </span>
            </div>
          </div>
        )}

        {isDynamic && (
          <div className="bg-bg p-3 rounded-md mb-3.5 help">
            Dynamisch contract: marktprijs per uur uit ENTSO‑E baseline, geen vaste tarieven.
            Inkoopvergoeding hieronder is opslag op import en bonus/kost op export.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3.5">
          <div className="field">
            <span className="label-up">Energiebelasting (€/kWh)</span>
            <input
              className="input"
              type="number"
              step="0.0001"
              value={draft.energiebelasting_eur_per_kwh}
              onChange={(e) =>
                update("energiebelasting_eur_per_kwh", parseFloat(e.target.value) || 0)
              }
            />
            <span className="field-help">2026: €0,0916. Wettelijk vast.</span>
          </div>
          <div className="field">
            <span className="label-up">BTW (%)</span>
            <input
              className="input"
              type="number"
              step="1"
              value={draft.btw_pct}
              onChange={(e) => update("btw_pct", parseInt(e.target.value) || 21)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3.5">
          <div className="field">
            <span className="label-up">Inkoopvergoeding (€/kWh)</span>
            <input
              className="input"
              type="number"
              step="0.001"
              value={draft.inkoopvergoeding_eur_per_kwh}
              onChange={(e) =>
                update("inkoopvergoeding_eur_per_kwh", parseFloat(e.target.value) || 0)
              }
            />
            <span className="field-help">Opslag op import, bonus/kost op export afhankelijk van handling.</span>
          </div>
          <div className="field">
            <span className="label-up">Bij teruglevering</span>
            <select
              className="input"
              value={draft.inkoopvergoeding_handling}
              onChange={(e) => update("inkoopvergoeding_handling", e.target.value as ContractIn["inkoopvergoeding_handling"])}
            >
              <option value="uitgekeerd">Uitgekeerd (bonus)</option>
              <option value="neutraal">Neutraal</option>
              <option value="afgetrokken">Afgetrokken (kost)</option>
            </select>
          </div>
        </div>

        {!isDynamic && (
          <div className="field mb-3.5">
            <span className="label-up">Niet‑saldering vergoeding (% van kale prijs)</span>
            <input
              className="input"
              type="number"
              step="1"
              value={draft.niet_saldering_pct}
              onChange={(e) => update("niet_saldering_pct", parseInt(e.target.value) || 50)}
            />
            <span className="field-help">Voor 2027+. Wettelijk min. 50% tot 2030.</span>
          </div>
        )}

        <hr className="divider" />
        <div className="label-up mb-2.5">Terugleverkosten</div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3.5">
          <div className="field">
            <span className="label-up">Model</span>
            <select
              className="input"
              value={draft.terugleverkosten_model}
              onChange={(e) => update("terugleverkosten_model", e.target.value as ContractIn["terugleverkosten_model"])}
            >
              <option value="geen">Geen (typisch dynamisch)</option>
              <option value="vast">Vast (€/maand)</option>
              <option value="per_kwh">Per kWh teruggeleverd</option>
            </select>
          </div>
          {draft.terugleverkosten_model === "vast" && (
            <div className="field">
              <span className="label-up">€/maand</span>
              <input
                className="input"
                type="number"
                step="0.5"
                value={draft.terugleverkosten_vast_eur_per_maand}
                onChange={(e) =>
                  update("terugleverkosten_vast_eur_per_maand", parseFloat(e.target.value) || 0)
                }
              />
            </div>
          )}
          {draft.terugleverkosten_model === "per_kwh" && (
            <div className="field">
              <span className="label-up">€/kWh</span>
              <input
                className="input"
                type="number"
                step="0.001"
                value={draft.terugleverkosten_per_kwh}
                onChange={(e) =>
                  update("terugleverkosten_per_kwh", parseFloat(e.target.value) || 0)
                }
              />
            </div>
          )}
        </div>

        <hr className="divider" />

        <div className="field mb-3.5">
          <span className="label-up">Notities</span>
          <input
            className="input input-text"
            value={draft.notes ?? ""}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="bv. bron, looptijd, voorwaarden"
          />
        </div>

        <hr className="divider" />

        {/* Live preview */}
        <div className="bg-bg p-3.5 rounded-lg mt-2">
          <div className="flex justify-between items-baseline mb-2">
            <span className="label-up">Berekening voor huidig scenario</span>
            <span className="bignum">{eur(preview.total_eur, 0)}</span>
          </div>
          <BreakdownRow
            label="Vastrecht"
            value={eur(bd.vastrecht_eur)}
            sub={`€${draft.vastrecht_eur_per_maand.toFixed(2)}/m × 12`}
          />
          <BreakdownRow
            label="Import (kosten)"
            value={eur(bd.import_marktprijs_eur)}
            sub={`${kwh(flow.year_totals.import_kwh)} import`}
            color="text-rust"
          />
          <BreakdownRow
            label="Belasting na saldering"
            value={eur(bd.import_belasting_eur)}
            sub={`korting door saldering: ${eur(bd.saldering_belasting_korting_eur)} (info)`}
            color="text-rust"
          />
          {bd.export_opbrengst_marktprijs_eur !== 0 && (
            <BreakdownRow
              label="Export opbrengst (marktprijs)"
              value={eur(bd.export_opbrengst_marktprijs_eur)}
              color={bd.export_opbrengst_marktprijs_eur > 0 ? "text-green" : "text-rust"}
              negate
            />
          )}
          {bd.inkoopvergoeding_export_eur !== 0 && (
            <BreakdownRow
              label="Inkoopvergoeding op export"
              value={eur(bd.inkoopvergoeding_export_eur)}
              color={bd.inkoopvergoeding_export_eur > 0 ? "text-green" : "text-rust"}
              negate
            />
          )}
          {bd.boven_plafond_opbrengst_eur > 0 && (
            <BreakdownRow
              label={scenario.year === 2026 ? "Overschot teruglevering" : "Teruglevering"}
              value={eur(bd.boven_plafond_opbrengst_eur)}
              sub={`${kwh(bd.boven_plafond_kwh)} ${scenario.year === 2026 ? "boven plafond" : ""}`}
              color="text-green"
              negate
            />
          )}
          {bd.terugleverkosten_eur > 0 && (
            <BreakdownRow
              label="Terugleverkosten"
              value={eur(bd.terugleverkosten_eur)}
              color="text-rust"
            />
          )}
          {bd.heffingskorting_eur > 0 && (
            <BreakdownRow
              label="Heffingskorting EB (wettelijk)"
              value={eur(bd.heffingskorting_eur)}
              sub="vermindering energiebelasting per huishouden, gelijk voor alle leveranciers"
              color="text-green"
              negate
            />
          )}
        </div>

        <div className="flex justify-between mt-4 gap-2 flex-wrap">
          {confirmingDelete ? (
            <div className="flex gap-2 items-center fade-in">
              <span className="text-sm text-rust font-medium">Echt verwijderen?</span>
              <button
                className="btn btn-danger"
                onClick={() => onDelete(contract.id)}
              >
                Ja, verwijder
              </button>
              <button className="btn" onClick={() => setConfirmingDelete(false)}>
                Annuleer
              </button>
            </div>
          ) : (
            <button
              className="btn btn-danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Verwijderen
            </button>
          )}
          <div className="flex gap-2">
            <button className="btn" onClick={onClose}>
              Annuleren
            </button>
            <button className="btn btn-primary" onClick={() => onSave(contract.id, draft)}>
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Disable unused variable warning for eurPrecise (kept for future)
  void eurPrecise;
}

function BreakdownRow({
  label,
  value,
  sub,
  color,
  negate,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  negate?: boolean;
}) {
  return (
    <div className="flex justify-between items-baseline py-2 border-b border-dashed border-line">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="help mt-0.5">{sub}</div>}
      </div>
      <div className={`mono text-sm font-semibold ${color ?? "text-ink"}`}>
        {negate ? "− " : ""}
        {value}
      </div>
    </div>
  );
}
